/**
 * Account fit display. Prefer `account_fit_label` from the API.
 * Fallback bands match marketIntel FIT_TIERS (do not use Strong ≥ 70):
 * Excellent ≥ 90, Strong ≥ 75, Moderate ≥ 60, Low below 60.
 * A null score is missing data, not Low.
 */

export function isAccountFitScored(score) {
  return score != null && score !== '' && !Number.isNaN(Number(score));
}

export function fallbackAccountFitLabel(score) {
  if (!isAccountFitScored(score)) return null;
  const n = Number(score);
  if (n >= 90) return 'Excellent Fit';
  if (n >= 75) return 'Strong Fit';
  if (n >= 60) return 'Moderate Fit';
  return 'Low Fit';
}

export function accountFitLabel(account) {
  const fromApi = String(account?.account_fit_label || '').trim();
  if (fromApi) return fromApi;
  return fallbackAccountFitLabel(account?.account_fit_score);
}

export function accountFitBadgeClass(label) {
  const l = String(label || '').toLowerCase();
  if (l.includes('excellent')) return 'bg-green-100 text-green-800 border-green-200';
  if (l.includes('strong')) return 'bg-blue-100 text-blue-800 border-blue-200';
  if (l.includes('moderate')) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  if (l.includes('low')) return 'bg-red-100 text-red-800 border-red-200';
  return 'bg-gray-100 text-gray-800 border-gray-200';
}

export function accountFitTextClass(label) {
  const l = String(label || '').toLowerCase();
  if (l.includes('excellent')) return 'text-green-800';
  if (l.includes('strong')) return 'text-blue-800';
  if (l.includes('moderate')) return 'text-yellow-800';
  if (l.includes('low')) return 'text-red-800';
  return 'text-gray-800';
}

export function resolveAccountFit(account) {
  const scored = isAccountFitScored(account?.account_fit_score);
  const label = scored ? accountFitLabel(account) : null;
  return {
    scored,
    score: scored ? Number(account.account_fit_score) : null,
    label,
    color: accountFitBadgeClass(label),
    textColor: accountFitTextClass(label),
  };
}

/**
 * Display-only: translation key for a known English fit label, or null.
 * The English label itself stays the value for colour logic; callers fall
 * back to the raw label when this returns null.
 */
const ACCOUNT_FIT_LABEL_KEYS = {
  'excellent fit': 'excellentFit',
  'strong fit': 'strongFit',
  'moderate fit': 'moderateFit',
  'low fit': 'lowFit',
  'weak fit': 'homeWeakFit',
  'no fit': 'homeNoFit',
};

export function accountFitLabelKey(label) {
  const l = String(label || '').trim().toLowerCase();
  return ACCOUNT_FIT_LABEL_KEYS[l] || null;
}
